/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-02-14 14:30:58
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, Popover, Stack, FormLayout, DatePicker, TextField } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { getStoreDate } from 'src/helpers/date-time-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';

type Props = WithI18nProps;

type State = {
  popoverActive: boolean,
  month: number,
  year: number,
  selectedDates: {
    start: Date | null,
    end: Date | null
  },
  disableDatesAfter: ?Date,
  disableDatesBefore: ?Date
};

class RenderDatePickerComponent extends Component<Props, State> {
  today = getStoreDate(new Date());

  state = {
    //Date changed from input
    date: '',
    emptyValue: false,
    popoverActive: false,
    month: this.today.getMonth(),
    year: this.today.getFullYear(),
    selectedDates: {
      start: null,
      end: null
    },
    disableDatesAfter: null,
    disableDatesBefore: null
  };

  componentDidMount() {
    // Init selected date for first loading
    let { selectedDates } = this.state;
    const { start } = selectedDates;
    const { start: startDate, end: endDate, appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    // Having date
    if (!start && startDate) {
      const date = moment(startDate).format(formatDateNumber);
      selectedDates.start = startDate;
      selectedDates.end = endDate;
      const month = startDate.getMonth();
      const year = startDate.getFullYear();
      this.setState({ selectedDates, date, month, year });
    }

    // First time of picking
    if (!start && !startDate) {
      const today: any = getStoreDate(new Date());
      selectedDates.start = today;
      selectedDates.end = today;
      const date = moment(today).format(formatDateNumber);
      this.setState({ selectedDates, date });
    }
  }

  render() {
    const {
      date,
      emptyValue,
      popoverActive,
      month,
      year,
      selectedDates,
      disableDatesBefore,
      disableDatesAfter
    } = this.state;
    const {
      input: { value },
      label,
      meta,
      appLanguage,
      disabled,
      i18n
    } = this.props;

    // Convert date object to string
    const displayDate = value ? moment(value).format(DATE_FORMAT[appLanguage].number) : '';

    const activator = (
      <div className="date-select">
        <div className="Polaris-Labelled__LabelWrapper">
          <div className="Polaris-Label">
            <span className="default-text-style Polaris-Label__Text" style={{ display: 'inline' }}>
              {label}
            </span>
          </div>
        </div>
        <Button disabled={disabled} onClick={this.togglePopoverActive} fullWidth textAlign="left">
          {displayDate}
        </Button>

        <div className="field-error-text">{emptyValue && meta.error}</div>
      </div>
    );

    return (
      <div className="">
        <Popover
          active={popoverActive}
          activator={activator}
          onClose={() => {
            this.togglePopoverActive();
            if (_.isEmpty(value)) {
              this.setState({ emptyValue: true });
            } else {
              this.setState({ emptyValue: false });
            }
          }}
          sectioned>
          <Popover.Pane>
            <Popover.Section>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    value={date}
                    onChange={this.handleChangeDate}
                    onBlur={this.handleBlurDate}
                  />
                </FormLayout.Group>
                <div>
                  <DatePicker
                    month={month}
                    year={year}
                    onChange={this.setSelectedDates}
                    onMonthChange={this.handleMonthChange}
                    selected={selectedDates}
                    multiMonth={false}
                    allowRange={false}
                    disableDatesAfter={disableDatesAfter}
                    disableDatesBefore={disableDatesBefore}
                  />
                </div>
              </FormLayout>
            </Popover.Section>
          </Popover.Pane>

          <Popover.Pane fixed>
            <Popover.Section>
              <div className="BC-Popover__Button">
                <Stack>
                  <Button onClick={this.togglePopoverActive}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                  <Button primary onClick={this.handleApply}>
                    {i18n.translate('AdminHTML.Common.Action.apply')}
                  </Button>
                </Stack>
              </div>
            </Popover.Section>
          </Popover.Pane>
        </Popover>
      </div>
    );
  }

  // Toggle state to show/hide the popover
  togglePopoverActive = () => {
    const { popoverActive } = this.state;

    // Clear selected date if canceling the calendar
    if (popoverActive) {
      this.setState({ popoverActive: !popoverActive });
    } else {
      this.setState({ popoverActive: !popoverActive });
    }
  };

  // Handle change date
  setSelectedDates = selectedDates => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const date = moment(selectedDates.start).format(formatDateNumber);

    this.setState({ selectedDates, date });
  };

  // Handle change month
  handleMonthChange = (month, year) => {
    this.setState({ month, year });
  };

  // Handle start date input data
  handleChangeDate = value => {
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;

    let { selectedDates } = this.state;
    const selected = moment(value, formatDateNumber).isValid()
      ? moment(value, formatDateNumber).toDate()
      : getStoreDate(new Date());
    selectedDates.start = selected;
    selectedDates.end = selected;

    //Change month, year
    this.handleMonthChange(selected.getMonth(), selected.getFullYear());
    this.setState({ date: value, selectedDates });
  };

  // Handle blur start date input data
  handleBlurDate = () => {
    // check if the start date is not valid, change to today/end date
    const { appLanguage } = this.props;
    const formatDateNumber = DATE_FORMAT[appLanguage].number;
    const {
      date,
      selectedDates: { start, end }
    } = this.state;

    if (!moment(date, formatDateNumber).isValid()) {
      const today = getStoreDate(getStoreDate(new Date()));
      if (moment(end).isAfter(today)) {
        this.handleChangeDate(moment(today).format(formatDateNumber));
      } else {
        this.handleChangeDate(moment(end).format(formatDateNumber));
      }
    } else {
      if (moment(start).isAfter(moment(end))) {
        this.handleChangeDate(moment(end).format(formatDateNumber));
      }
    }
  };

  // Handle apply button
  handleApply = () => {
    const { onChooseDate, index } = this.props;
    const { selectedDates } = this.state;

    // Save object date
    onChooseDate(selectedDates, index);

    // Close popover
    this.togglePopoverActive();
  };
}

const mapStateToProps = (state, props) => ({
  appLanguage: _.get(state.oauth.tenantApp, 'appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
});

export const RenderDatePicker = withI18n()(connect(mapStateToProps, {})(RenderDatePickerComponent));
